/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useReducer, useEffect, useContext } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { GET_SURVEYS } from "queries/queries";
import { ANSWER_QUESTION, FINISH_SURVEY_QUESTIONNAIRE } from "queries/mutations";

interface IAnswerOptions {}

interface IUserAnswers {}

interface IQuestionValues {
  id: string;
  questionType: string;
  text?: string;
  sequenceNumber: number;
  sectionId: string;
  required: boolean;
  answerOptions: IAnswerOptions[];
  from: string | null;
  till: string | null;
  user_answers: IUserAnswers[];
}

interface ISurvey {
  id: string;
  name: string;
  description: string;
  active: boolean;
  credits: number;
  questions: IQuestionValues[];
}

interface ISurveysContextValue {
  error: boolean,
  questions: IQuestionValues[];
  survey: ISurvey;
  loadingAnswer: boolean;
  surveys: ISurvey[];
  loading: boolean;
  currentIndex: number;
  status: "idle" | "questions" | "completed";
  begin: () => void;
  next: () => void;
  previous: () => void;
  reset: () => void;
  initSurvey: (surveyId: string) => void;
  addAnswer: (questionId: string, answer: IAnswer[]) => void;
}

interface IAnswer {
  userId: string;
  questionId: string;
  answerText?: string;
  value?: string;
}

const initialValues: ISurveysContextValue = {
  questions: [],
  surveys: [],
  survey: null,
  loading: true,
  loadingAnswer: false,
  error: false,
  currentIndex: 0,
  status: "idle", //idle, questions, completed
  begin: () => {},
  next: () => {},
  previous: () => {},
  reset: () => {},
  initSurvey: () => {},
  addAnswer: (questionId: string, answer: IAnswer[]) => {},
};

const questionReducer = (state: ISurveysContextValue, action) => {
  switch (action.type) {
    case "fetchSurveys":
      return {
        ...state,
        surveys: action.payload.surveys,
        loading: false,
      };
    case "initSurvey":
      return {
        ...state,
        survey: action.payload.survey,
        questions: action.payload.questions,
        loading: false,
      };
    case "forward":
      return {
        ...state,
        currentIndex:
          Math.min(state.currentIndex + 1, state.questions.length - 1),
      };
    case "completed":
      return {
        ...state,
        status: "completed" as const,
      };
    case "backward":
      return {
        ...state,
        currentIndex: Math.max(state.currentIndex, 1) - 1,
        status: "questions" as const,
      };
    case "begin":
      return {
        ...state,
        status: "questions" as const,
      };
    case "addAnswer":
      return {
        ...state,
        questions: state.questions.map((eachQuestion) => {
          if (eachQuestion.id === action.payload.questionId) {
            return {
              ...eachQuestion,
              user_answers: action.payload.answer,
            };
          } else {
            return eachQuestion;
          }
        }),
      };
    case 'networkAnswer':
      return {
        ...state,
        loadingAnswer: action.payload.status,
      }
    case "reset":
      return {
        ...state,
        currentIndex: 0,
        status: "idle" as const,
      }
      case "error":
      return {
        ...state,
        loading: false,
        error: true,
      }
    default:
      return state;
  }
};

const SurveyContext = createContext(initialValues);

export default function SurveyProvider({ children }) {
  const [state, dispatch] = useReducer(questionReducer, initialValues);
  const { loading, error, data, refetch } = useQuery(GET_SURVEYS, {
    fetchPolicy: "no-cache",
  });



  const [answerMutationFunction, { error:answerError }] = useMutation(ANSWER_QUESTION);
  const [finishMutationFunction] = useMutation(FINISH_SURVEY_QUESTIONNAIRE);


  const initSurvey = async (surveyId) => {
    try {
      if(!state.survey) {
        const result = await refetch();
        const survey = result.data.questionnaires.find((each) => each.id === surveyId);
    
        dispatch({
         type: "initSurvey",
         payload: {
           survey,
           questions: survey?.questions || [],
         },
       });
       return
       }
       const survey = state.surveys.find((each) => each.id === surveyId);
         
       dispatch({
         type: "initSurvey",
         payload: {
           survey,
           questions: survey?.questions || [],
         },
       });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: {error}
      })
    }
  };

  const fetchSurveys = () => {
    if(data) {
      dispatch({
        type: 'fetchSurveys',
        payload: {surveys: data.questionnaires}
      })
    }
  }

  useEffect(() => {
    fetchSurveys()
  }, [data, error, loading]);

  const next = () => {
    if(state.currentIndex >= (state.questions.length -1)){

      dispatch({
        type: 'completed'
      })
    } else {
      dispatch({
        type: "forward",
      });
    }
  };

  const previous = () => {      
    dispatch({
      type: "backward",
    });
  };

  const addAnswer = async (questionId: string, answer: IAnswer[]) => {
    const isLast = state.currentIndex >= (state.questions.length -1);
    try {
      dispatch({
        type: "addAnswer",
        payload: {
          answer,
          questionId,
        },
      });
      dispatch({
        type: 'networkAnswer',
        payload: {
          status: true,
        }
      })
      const answerPromise =  answerMutationFunction({
        variables: {
          questionId: questionId,
          answers:  JSON.stringify(answer.map(each => each.answerText))
        }
      })
      const surveyMutationsPromises = [];
      surveyMutationsPromises.push(answerPromise)

      if(isLast) {
        const finishSurveyPromise = finishMutationFunction({
          variables: {
            questionnaireId: state.survey.id
          }
        })
        surveyMutationsPromises.push(finishSurveyPromise)
      }

      await Promise.all(surveyMutationsPromises)
      await refetch();

      dispatch({
        type: 'networkAnswer',
        payload: {
          status: false,
        }
      })
      if(!answerError){
        return true;
      }else{
        console.error(answerError)
        return false;
      }
        
    } catch (error) {
      dispatch({
        type: 'networkAnswer',
        payload: {
          status: false,
        }
      })
      console.error(error)
      return false;
    }
  };

  const begin = () => {
    dispatch({
      type: "begin",
    });
  };

  const reset = async () => {
    await refetch();
    dispatch({
      type: 'reset',
    })
  };

  return (
    <SurveyContext.Provider
      value={{ ...state, next, previous, addAnswer, begin, initSurvey, reset }}
    >
      {children}
    </SurveyContext.Provider>
  );
}

export const useSurveyDetail = () => {
  const surveyContext = useContext(SurveyContext);
  if (!surveyContext) {
    throw new Error(
      "Missing SurveyProvider. You cannot use the hook, without the provider."
    );
  }

  return surveyContext;
};
